import { Helmet } from '@dr.pogodin/react-helmet';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from "react-router";
import { motion } from 'motion/react';
import { Star, ArrowRight, ChevronRight, Play, ChevronLeft, ChevronRight as ChevronRightIcon, Send, Phone, Mail, MapPin, CheckCircle } from 'lucide-react';
import { home } from 'virtual:content';
import EmailSignupForm from '@/components/EmailSignupForm';
import DeferredVideo from '@/components/DeferredVideo';
import { imgSrc } from '@/lib/image-slots';
import { trackWatchVideo } from '@/lib/pinterest';
const site = 'https://www.sandandsuntravels.com';
const DEST_IMGS = ['destinations/maldives', 'destinations/amalfi-coast', 'destinations/bali', 'destinations/caribbean', 'destinations/alaska-cruise'];
const SVC_IMGS = [imgSrc('services/honeymoon'), imgSrc('services/family-vacation'), imgSrc('destinations/alaska-cruise'), imgSrc('services/adventure-travel')];
const destinationSrcSet = (slot: string, height: number, widths: number[]) =>
  widths.map((width) => `${imgSrc(slot, { width, height, fit: 'crop' })} ${width}w`).join(', ');
const VIDEO_TESTIMONIALS = [{
  id: 1,
  videoSrc: '/airo-assets/videos/testimonials/video-1',
  name: 'Sarah & Michael T.',
  destination: 'Maldives Honeymoon',
  quote: 'Sand & Sun made our honeymoon absolutely magical. Every detail was taken care of — we just showed up and fell in love all over again.',
  rating: 5
}, {
  id: 2,
  videoSrc: '/airo-assets/videos/testimonials/video-2',
  name: 'The Rodriguez Family',
  destination: 'Bali Family Escape',
  quote: 'Traveling with three kids felt impossible until we found Sand & Sun. They planned everything perfectly — the kids are still talking about it!',
  rating: 5
}, {
  id: 3,
  videoSrc: '/airo-assets/videos/testimonials/video-3',
  name: 'James K.',
  destination: 'Costa Rica Adventure',
  quote: 'I wanted something off the beaten path and they delivered beyond my wildest expectations. The most memorable trip of my life.',
  rating: 5
}, {
  id: 4,
  videoSrc: '/airo-assets/videos/testimonials/video-4',
  name: 'Linda & Robert M.',
  destination: 'Santorini Getaway',
  quote: 'From the first call to the last sunset, everything was flawless. We\'ve already booked our next trip with them.',
  rating: 5
}] as const;
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut' as const
    }
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
function ContactSection() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErrorMsg('Please fill in your name, email, and message.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    const FRIENDLY_ERROR = 'Something went wrong. Please try again or call or WhatsApp us at (303) 720-5721.';
    try {
      const messageBody = [form.message, form.phone ? `Phone: ${form.phone}` : ''].filter(Boolean).join('\n\n');
      const res = await fetch('/api/contact/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user: {
            email: form.email,
            name: form.name
          },
          conversation: {
            messages_attributes: [{
              body: messageBody
            }],
            data: {
              source: 'homepage-contact-form',
              phone: form.phone
            }
          }
        })
      });

      // Always read as text first — CDN/proxy may return HTML 502 instead of JSON
      const raw = await res.text().catch(() => '');
      const isJson = res.headers.get('content-type')?.includes('application/json') ?? false;
      if (!res.ok) {
        setErrorMsg(FRIENDLY_ERROR);
        setStatus('error');
        return;
      }
      if (isJson && raw) {
        let json: {
          success?: boolean;
        } = {};
        try {
          json = JSON.parse(raw) as typeof json;
        } catch {/* treat as success */}
        if (json.success === false) {
          setErrorMsg(FRIENDLY_ERROR);
          setStatus('error');
          return;
        }
      }
      setStatus('success');
      setForm({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch {
      setErrorMsg(FRIENDLY_ERROR);
      setStatus('error');
    }
  };
  return <section className="contact-section py-24 bg-muted relative overflow-hidden">

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section header */}
        <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-[#00E5FF] text-sm font-semibold tracking-widest uppercase mb-3">
            Get in Touch
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white font-heading">
            We'd Love to Hear From You
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-lg mt-4 max-w-xl mx-auto">
            Have a question or ready to start planning? Send us a message and we'll get back to you within one business day.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start max-w-5xl mx-auto">
          {/* Contact info */}
          <motion.div className="lg:col-span-2 space-y-8" initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut' as const
        }}>
            <div>
              <h3 className="font-heading text-white text-xl font-bold mb-6">
                Contact Information
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 card-primary-glow">
                    <Phone size={16} className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Phone</p>
                    <a href="tel:+13037205721" className="text-white font-semibold hover:text-[#00E5FF] transition-colors">
                      (303) 720-5721
                    </a>
                    <p className="text-white/40 text-xs mt-0.5">(303) 720-5721</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 card-primary-glow">
                    <Mail size={16} className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Email</p>
                    <a href="mailto:info@sandandsuntravels.com" className="text-white font-semibold hover:text-[#00E5FF] transition-colors">
                      info@sandandsuntravels.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 card-primary-glow">
                    <MapPin size={16} className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Location</p>
                    <p className="text-white font-semibold">Denver, Colorado</p>
                    <p className="text-white/40 text-xs mt-0.5">Serving travelers nationwide</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div className="pt-6 border-t border-white/10">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-4">Quick Links</p>
              <div className="space-y-2">
                <Link to="/quote" className="flex items-center gap-2 text-white/70 hover:text-[#00E5FF] text-sm transition-colors group">
                  <ChevronRight size={14} className="text-[#00E5FF] group-hover:translate-x-0.5 transition-transform" />
                  Request a Free Quote
                </Link>
                <Link to="/destinations" className="flex items-center gap-2 text-white/70 hover:text-[#00E5FF] text-sm transition-colors group">
                  <ChevronRight size={14} className="text-[#00E5FF] group-hover:translate-x-0.5 transition-transform" />
                  Browse Destinations
                </Link>
                <Link to="/faq" className="flex items-center gap-2 text-white/70 hover:text-[#00E5FF] text-sm transition-colors group">
                  <ChevronRight size={14} className="text-[#00E5FF] group-hover:translate-x-0.5 transition-transform" />
                  Frequently Asked Questions
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div className="lg:col-span-3" initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut' as const
        }}>
            <div className="bg-[#060e18] rounded-2xl p-8 border border-white/10 shadow-2xl">
              {status === 'success' ? <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5 card-primary-glow-strong">
                    <CheckCircle size={32} className="text-[#00E5FF]" />
                  </div>
                  <h3 className="font-heading text-white text-2xl font-bold mb-3">
                    Message Sent!
                  </h3>
                  <p className="text-white/60 text-base max-w-sm">
                    Thanks for reaching out. We'll get back to you within one business day.
                  </p>
                  <button onClick={() => setStatus('idle')} className="mt-8 text-[#00E5FF] text-sm font-semibold hover:underline">
                    Send another message
                  </button>
                </div> : <form onSubmit={handleSubmit} noValidate>
                  <h3 className="font-heading text-white text-xl font-bold mb-6">
                    Send Us a Message
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-white/50 text-xs uppercase tracking-widest mb-2">
                        Full Name <span className="text-[#ff1a00]">*</span>
                      </label>
                      <input id="contact-name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="Jane Smith" required className="w-full bg-[#0a1420] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#00E5FF]/50 transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-white/50 text-xs uppercase tracking-widest mb-2">
                        Email <span className="text-[#ff1a00]">*</span>
                      </label>
                      <input id="contact-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@example.com" required className="w-full bg-[#0a1420] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#00E5FF]/50 transition-colors" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-phone" className="block text-white/50 text-xs uppercase tracking-widest mb-2">
                      Phone <span className="text-white/30">(optional)</span>
                    </label>
                    <input id="contact-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(555) 000-0000" className="w-full bg-[#0a1420] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#00E5FF]/50 transition-colors" />
                  </div>
                  <div className="mb-6">
                    <label htmlFor="contact-message" className="block text-white/50 text-xs uppercase tracking-widest mb-2">
                      Message <span className="text-[#ff1a00]">*</span>
                    </label>
                    <textarea id="contact-message" name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your dream trip, questions, or anything else..." required rows={5} className="w-full bg-[#0a1420] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#00E5FF]/50 transition-colors resize-none" />
                  </div>

                  {errorMsg && <p className="text-[#ff1a00] text-sm mb-4">{errorMsg}</p>}

                  <button type="submit" disabled={status === 'submitting'} className="w-full flex items-center justify-center gap-2 py-4 bg-[#00E5FF] text-[#0a1420] font-bold text-base rounded-xl hover:bg-[#00c8e0] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed">
                    {status === 'submitting' ? <>
                        <span className="w-4 h-4 border-2 border-[#0a1420]/30 border-t-[#0a1420] rounded-full animate-spin" />
                        Sending...
                      </> : <>
                        <Send size={16} />
                        Send Message
                      </>}
                  </button>
                  <p className="text-white/30 text-xs text-center mt-4">
                    We respond within one business day · No spam, ever
                  </p>
                </form>}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
export default function HomePage() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const totalSlides = VIDEO_TESTIMONIALS.length;
  const goTo = useCallback((idx: number) => {
    setActiveSlide((idx % totalSlides + totalSlides) % totalSlides);
  }, [totalSlides]);
  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % totalSlides);
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paused, totalSlides]);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [{
      '@type': 'WebSite',
      '@id': `${site}/#website`,
      name: 'Sand and Sun Travels',
      url: `${site}/`,
      inLanguage: ['en', 'es'],
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${site}/destinations?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    }, {
      '@type': ['TravelAgency', 'LocalBusiness'],
      '@id': `${site}/#organization`,
      name: 'Sand and Sun Travels',
      alternateName: 'Sand & Sun Travels',
      legalName: 'Sand and Sun Travels LLC',
      url: `${site}/`,
      logo: {
        '@type': 'ImageObject',
        url: `${site}/airo-assets/images/logo/horizontal`,
        width: 800,
        height: 200
      },
      image: `${site}/airo-assets/images/pages/home/og-image`,
      description: 'Sand and Sun Travels LLC is a Denver-based travel agency dedicated to turning your travel dreams into unforgettable experiences. We specialize in Caribbean cruises, all-inclusive resorts, luxury yacht voyages, and custom vacation planning tailored to your unique style and budget. As a Fora Certified Travel Advisor, we handle every detail — from research and bookings to insider tips and personalized itineraries — so you can focus on making memories.',
      foundingDate: '2025',
      numberOfEmployees: {
        '@type': 'QuantitativeValue',
        value: 4
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '6357 E 143rd Ave',
        addressLocality: 'Thornton',
        addressRegion: 'CO',
        postalCode: '80602',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 39.9336,
        longitude: -104.8897
      },
      hasMap: 'https://maps.google.com/?q=6357+E+143rd+Ave+Thornton+CO+80602',
      telephone: '+13037205721',
      email: 'info@sandandsuntravels.com',
      openingHours: 'Mo-Su 07:00-20:00',
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '07:00',
        closes: '20:00'
      }],
      priceRange: '$$',
      currenciesAccepted: 'USD',
      paymentAccepted: 'Cash, Credit Card, Debit Card',
      areaServed: [{
        '@type': 'Country',
        name: 'United States'
      }, {
        '@type': 'City',
        name: 'Denver',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }, {
        '@type': 'City',
        name: 'Brighton',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }, {
        '@type': 'City',
        name: 'Loveland',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }, {
        '@type': 'City',
        name: 'Superior',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }, {
        '@type': 'City',
        name: 'Thornton',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }, {
        '@type': 'City',
        name: 'Westminster',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }, {
        '@type': 'City',
        name: 'Highlands Ranch',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }, {
        '@type': 'City',
        name: 'Cherry Creek',
        containedInPlace: { '@type': 'State', name: 'Colorado' }
      }],
      serviceArea: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: 39.9336,
          longitude: -104.8897
        },
        geoRadius: '50000'
      },
      knowsLanguage: ['en', 'es'],
      sameAs: ['https://www.facebook.com/profile.php?id=61591985321929', 'https://www.instagram.com/sandandsuntravels1/'],
      contactPoint: [{
        '@type': 'ContactPoint',
        telephone: '+13037205721',
        contactType: 'customer service',
        availableLanguage: ['English', 'Spanish'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '07:00',
          closes: '20:00'
        }
      }, {
        '@type': 'ContactPoint',
        email: 'info@sandandsuntravels.com',
        contactType: 'sales',
        availableLanguage: ['English', 'Spanish']
      }],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        bestRating: '5',
        worstRating: '1',
        ratingCount: '3',
        reviewCount: '3'
      },
      review: [{
        '@type': 'Review',
        '@id': `${site}/#review-1`,
        author: {
          '@type': 'Person',
          name: 'Sarah & James M.'
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        },
        reviewBody: 'Sand and Sun turned our honeymoon dream into something even better than we imagined. Every detail was perfect — from the overwater villa to the private sunset dinner on the beach.',
        name: 'Maldives Honeymoon',
        itemReviewed: {
          '@id': `${site}/#organization`
        }
      }, {
        '@type': 'Review',
        '@id': `${site}/#review-2`,
        author: {
          '@type': 'Person',
          name: 'The Rodriguez Family'
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        },
        reviewBody: 'Our family of five had the most incredible two weeks in Bali and Thailand. The kids are still talking about it. Worth every penny and then some.',
        name: 'Bali & Thailand Family Tour',
        itemReviewed: {
          '@id': `${site}/#organization`
        }
      }, {
        '@type': 'Review',
        '@id': `${site}/#review-3`,
        author: {
          '@type': 'Person',
          name: 'Michael T.'
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        },
        reviewBody: "I've used travel agents before, but never one like this. They found experiences I didn't even know existed. The Amalfi Coast trip was the best week of my life.",
        name: 'Amalfi Coast & Rome',
        itemReviewed: {
          '@id': `${site}/#organization`
        }
      }]
    }, {
      '@type': 'WebPage',
      '@id': `${site}/#webpage`,
      url: `${site}/`,
      name: 'Sand and Sun Travels — Dream Destinations, Expert Advisors',
      inLanguage: 'en',
      isPartOf: {
        '@id': `${site}/#website`
      },
      about: {
        '@id': `${site}/#organization`
      },
      datePublished: '2025-01-01',
      dateModified: '2026-07-23'
    }]
  };
  return <>
      <Helmet>
        <title>Sand and Sun Travels — Luxury Travel Advisors</title>
        <meta name="description" content="Expert bilingual travel advisors planning honeymoons, family vacations & luxury cruises to the Maldives, Bali, Santorini, Caribbean & beyond. Get a free quote today." />
        <meta name="keywords" content="travel agency, honeymoon travel, family vacation planner, cruise packages, adventure travel, luxury travel agency Denver, tropical vacations, Maldives honeymoon, Caribbean cruise, Bali vacation" />
        <link rel="canonical" href={`${site}/`} />
        <link rel="alternate" hrefLang="en" href={`${site}/`} />
        <link rel="alternate" hrefLang="es" href={`${site}/es`} />
        <link rel="alternate" hrefLang="x-default" href={`${site}/`} />
        <meta property="og:site_name" content="Sand and Sun Travels" />
        <meta property="og:locale" content="en_US" />
        <link
          rel="preload"
          as="image"
          href={imgSrc('pages/home/hero-poster', { width: 1280, height: 720, fit: 'crop' })}
          imageSrcSet={destinationSrcSet('pages/home/hero-poster', 720, [768, 1280, 1600])}
          imageSizes="100vw"
          fetchPriority="high"
        />
        <meta property="og:locale:alternate" content="es_ES" />
        <meta property="og:title" content="Sand and Sun Travels — Dream Destinations, Expert Advisors" />
        <meta property="og:description" content="Expert travel advisors crafting unforgettable journeys to the world's most breathtaking destinations. Honeymoons, family trips, cruises, and adventure travel." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${site}/`} />
        <meta property="og:image" content={`${site}/airo-assets/images/pages/home/og-image`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Sand and Sun Travels — luxury travel agency" />
        <meta property="og:image:secure_url" content={`${site}/airo-assets/images/pages/home/og-image`} />
        <meta property="article:publisher" content="https://www.facebook.com/profile.php?id=61591985321929" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@sandandsuntravels" />
        <meta name="twitter:creator" content="@sandandsuntravels" />
        <meta name="twitter:title" content="Sand and Sun Travels — Dream Destinations, Expert Advisors" />
        <meta name="twitter:description" content="Expert travel advisors crafting unforgettable journeys to the world's most breathtaking destinations. Honeymoons, family trips, cruises, and adventure travel." />
        <meta name="twitter:image" content={`${site}/airo-assets/images/pages/home/og-image`} />
        <meta name="twitter:image:alt" content="Sand and Sun Travels — luxury travel agency" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <main>
        {/* ── HERO ── */}
        <section className="relative min-h-[100svh] md:min-h-[92vh] flex items-center overflow-hidden">
          {/* Background Video */}
          <div className="absolute inset-0">
            <DeferredVideo
              src="/airo-assets/videos/pages/home/hero"
              poster={imgSrc('pages/home/hero-poster', { width: 1280, height: 720, fit: 'crop' })}
              posterSrcSet={destinationSrcSet('pages/home/hero-poster', 720, [768, 1280, 1600])}
              posterSizes="100vw"
              alt="Tropical island travel destination"
              captionsSrc="/captions/home-hero.en.vtt"
              className="w-full h-full object-cover"
              width={1920}
              height={1080}
              priority
              onPlay={() => trackWatchVideo('Sand and Sun Travels – Hero')}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 container mx-auto px-5 lg:px-8 pt-28 pb-16 md:py-24">
            <motion.div className="max-w-3xl" initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-[#00E5FF] text-xs md:text-sm font-semibold tracking-widest uppercase mb-3 md:mb-4">
                Sand and Sun Travels
              </motion.p>
              <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] mb-4 md:mb-6 font-heading">
                {home.hero.headline}
              </h1>
              <motion.p variants={fadeUp} className="text-white/75 text-base md:text-xl leading-relaxed mb-7 md:mb-10 max-w-xl">
                {home.hero.subheadline}
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6 md:mb-14">
                <Link to="/quote" className="w-full sm:w-auto text-center px-8 py-4 bg-[#00E5FF] text-[#0a1420] font-bold text-base rounded-full hover:bg-[#00c8e0] transition-all duration-200 hover:scale-105 shadow-lg shadow-[#00E5FF]/25">
                  {home.hero.cta}
                </Link>
                <Link to="/destinations" className="w-full sm:w-auto text-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold text-base rounded-full border border-white/25 hover:bg-white/20 transition-all duration-200 flex items-center justify-center gap-2">
                  {home.hero.ctaSecondary}
                  <ChevronRight size={16} />
                </Link>
                {/* Mobile-only tap-to-call + WhatsApp */}
                <a href="tel:+13037205721" className="sm:hidden w-full text-center px-8 py-4 bg-white/5 text-white font-semibold text-base rounded-full border border-white/20 hover:bg-white/10 transition-all duration-200 flex items-center justify-center gap-2">
                  <Phone size={16} className="text-primary" />
                  Call Us: (303) 720-5721
                </a>
                <a href="https://api.whatsapp.com/send/?phone=13037205721&type=phone_number" target="_blank" rel="noopener noreferrer" className="sm:hidden w-full text-center px-8 py-4 bg-white/5 text-white font-semibold text-base rounded-full border border-white/20 hover:bg-white/10 transition-all duration-200 flex items-center justify-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-primary" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                  WhatsApp: (303) 720-5721
                </a>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-6">
                {home.hero.trustItems.map((item, i) => <div key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF]" />
                    <span className="text-white/70 text-sm font-medium">{item}</span>
                  </div>)}
              </motion.div>
            </motion.div>
          </div>

          {/* Scroll indicator — hidden on mobile */}
          <motion.div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.5,
          duration: 0.6
        }}>
            <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
            <motion.div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" animate={{
            scaleY: [1, 0.5, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut' as const
          }} />
          </motion.div>
        </section>

        {/* ── WHY BOOK WITH US ── */}
        <section className="py-16 md:py-20 bg-card border-b border-border">
          <div className="container mx-auto px-5 lg:px-8">
            <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-60px'
          }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-primary text-xs font-semibold tracking-widest uppercase mb-3">
                Why Sand &amp; Sun
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-bold text-foreground font-heading">
                Travel Smarter. Travel Better.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-muted-foreground text-base md:text-lg mt-4 max-w-2xl mx-auto">
                We're not a booking engine — we're your personal travel team. Here's what sets us apart.
              </motion.p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[{
              icon: '🌍',
              title: 'Bilingual Service',
              desc: 'Fluent English & Spanish advisors — no language barriers, ever.'
            }, {
              icon: '✈️',
              title: 'Fora-Certified Advisors',
              desc: 'Backed by Fora Travel, one of the top host agencies in the US.'
            }, {
              icon: '💎',
              title: 'Exclusive Perks',
              desc: "VIP upgrades, resort credits, and amenities you can't book online."
            }, {
              icon: '📞',
              title: 'Real Human Support',
              desc: 'A real person answers — before, during, and after your trip.'
            }].map((item, i) => <motion.div key={i} className="rounded-2xl p-6 border border-border hover:border-primary/40 transition-colors duration-300 bg-background/40" initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: 'easeOut' as const
            }}>
                  <span className="text-3xl block mb-3" role="img" aria-hidden="true">{item.icon}</span>
                  <h3 className="font-heading text-foreground font-bold text-lg mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </motion.div>)}
            </div>

            {/* Social proof bar */}
            <motion.div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-center" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }}>
              {[{
              value: '500+',
              label: 'Trips Planned'
            }, {
              value: '20+',
              label: 'Countries Covered'
            }, {
              value: '5★',
              label: 'Average Rating'
            }, {
              value: '100%',
              label: 'Free to Book'
            }].map((stat, i) => <div key={i} className="flex flex-col items-center gap-1">
                  <span className="font-heading text-2xl md:text-3xl font-bold text-primary">
                    {stat.value}
                  </span>
                  <span className="text-muted-foreground text-xs tracking-wide uppercase">{stat.label}</span>
                </div>)}
            </motion.div>
          </div>
        </section>

        {/* ── FEATURED DESTINATIONS ── */}
        <section className="py-24 bg-[#0a1420]">
          <div className="container mx-auto px-4 lg:px-8">
            {/* Section Header */}
            <motion.div className="mb-14" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-[#00E5FF] text-sm font-semibold tracking-widest uppercase mb-3">
                {home.destinations.sectionLabel}
              </motion.p>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-bold text-white font-heading">
                  {home.destinations.heading}
                </motion.h2>
                <motion.div variants={fadeUp}>
                  <Link to="/destinations" className="inline-flex items-center gap-2 text-[#00E5FF] font-semibold text-sm hover:gap-3 transition-all duration-200">
                    View all destinations <ArrowRight size={16} />
                  </Link>
                </motion.div>
              </div>
              <motion.p variants={fadeUp} className="text-white/50 text-lg mt-3 max-w-xl">
                {home.destinations.subheading}
              </motion.p>
            </motion.div>

            {/* Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[280px]">
              {/* Large card 1 — Maldives */}
              <motion.div className="lg:col-span-2 lg:row-span-1 relative rounded-2xl overflow-hidden group cursor-pointer" initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              ease: 'easeOut' as const
            }} whileHover={{
              scale: 1.01
            }}>
              <Link to="/destinations/maldives" className="absolute inset-0 z-10" aria-label="Explore Maldives" />
                <img src={imgSrc(DEST_IMGS[0], { width: 640, height: 280, fit: 'crop' })} srcSet={destinationSrcSet(DEST_IMGS[0], 280, [480, 640, 800])} sizes="(min-width: 1024px) min(66vw, 853px), (min-width: 768px) 50vw, 100vw" alt="Maldives overwater bungalows" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" width={800} height={280} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                <div className="home-destination-caption absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[#00E5FF] text-xs font-semibold tracking-widest uppercase mb-1">
                    {home.destinations.items[0].region}
                  </p>
                  <h3 className="font-heading text-white text-2xl font-bold mb-2">
                    {home.destinations.items[0].name}
                  </h3>
                  <p className="text-white/70 text-sm max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500">
                    {home.destinations.items[0].teaser}
                  </p>
                </div>
              </motion.div>

              {/* Small card — Bali */}
              <motion.div className="relative rounded-2xl overflow-hidden group cursor-pointer" initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              delay: 0.1,
              ease: 'easeOut' as const
            }} whileHover={{
              scale: 1.02
            }}>
              <Link to="/destinations/bali" className="absolute inset-0 z-10" aria-label="Explore Bali" />
                <img src={imgSrc(DEST_IMGS[2], { width: 480, height: 280, fit: 'crop' })} srcSet={destinationSrcSet(DEST_IMGS[2], 280, [320, 480, 640])} sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw" alt="Bali rice terraces" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" width={400} height={280} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                <div className="home-destination-caption absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-[#00E5FF] text-xs font-semibold tracking-widest uppercase mb-1">
                    {home.destinations.items[2].region}
                  </p>
                  <h3 className="font-heading text-white text-xl font-bold">
                    {home.destinations.items[2].name}
                  </h3>
                  <p className="text-white/70 text-sm max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500">
                    {home.destinations.items[2].teaser}
                  </p>
                </div>
              </motion.div>

              {/* Small card — Caribbean */}
              <motion.div className="relative rounded-2xl overflow-hidden group cursor-pointer" initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              delay: 0.15,
              ease: 'easeOut' as const
            }} whileHover={{
              scale: 1.02
            }}>
              <Link to="/destinations/caribbean" className="absolute inset-0 z-10" aria-label="Explore Caribbean" />
                <img src={imgSrc(DEST_IMGS[3], { width: 480, height: 280, fit: 'crop' })} srcSet={destinationSrcSet(DEST_IMGS[3], 280, [320, 480, 640])} sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw" alt="Caribbean turquoise water" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" width={400} height={280} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                <div className="home-destination-caption absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-[#00E5FF] text-xs font-semibold tracking-widest uppercase mb-1">
                    {home.destinations.items[3].region}
                  </p>
                  <h3 className="font-heading text-white text-xl font-bold">
                    {home.destinations.items[3].name}
                  </h3>
                  <p className="text-white/70 text-sm max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500">
                    {home.destinations.items[3].teaser}
                  </p>
                </div>
              </motion.div>

              {/* Large card 2 — Amalfi Coast */}
              <motion.div className="lg:col-span-2 relative rounded-2xl overflow-hidden group cursor-pointer" initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              delay: 0.2,
              ease: 'easeOut' as const
            }} whileHover={{
              scale: 1.01
            }}>
              <Link to="/destinations/amalfi-coast" className="absolute inset-0 z-10" aria-label="Explore Amalfi Coast" />
                <img src={imgSrc(DEST_IMGS[1], { width: 640, height: 280, fit: 'crop' })} srcSet={destinationSrcSet(DEST_IMGS[1], 280, [480, 640, 800])} sizes="(min-width: 1024px) min(66vw, 853px), (min-width: 768px) 50vw, 100vw" alt="Amalfi Coast cliffside" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" width={800} height={280} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                <div className="home-destination-caption absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[#00E5FF] text-xs font-semibold tracking-widest uppercase mb-1">
                    {home.destinations.items[1].region}
                  </p>
                  <h3 className="font-heading text-white text-2xl font-bold mb-2">
                    {home.destinations.items[1].name}
                  </h3>
                  <p className="text-white/70 text-sm max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500">
                    {home.destinations.items[1].teaser}
                  </p>
                </div>
              </motion.div>

              {/* Small card — Alaska Cruise */}
              <motion.div className="relative rounded-2xl overflow-hidden group cursor-pointer" initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              delay: 0.25,
              ease: 'easeOut' as const
            }} whileHover={{
              scale: 1.02
            }}>
              <Link to="/destinations/alaska-cruise" className="absolute inset-0 z-10" aria-label="Explore Alaska Cruise" />
                <img src={DEST_IMGS[4]} alt="Alaska cruise glacier" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" width={400} height={280} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
                <div className="home-destination-caption absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-[#00E5FF] text-xs font-semibold tracking-widest uppercase mb-1">
                    {home.destinations.items[4].region}
                  </p>
                  <h3 className="font-heading text-white text-xl font-bold">
                    {home.destinations.items[4].name}
                  </h3>
                  <p className="text-white/70 text-sm max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500">
                    {home.destinations.items[4].teaser}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── SERVICES ── */}
        <section className="py-24 bg-[#060e18]">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-[#00E5FF] text-sm font-semibold tracking-widest uppercase mb-3">
                {home.services.sectionLabel}
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-bold text-white max-w-2xl font-heading">
                {home.services.heading}
              </motion.h2>
            </motion.div>

            {/* Alternating service rows */}
            <div className="space-y-6">
              {home.services.items.map((service, i) => <motion.div key={service.id} className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} rounded-2xl overflow-hidden min-h-[320px] group`} initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              delay: i * 0.08,
              ease: 'easeOut' as const
            }}>
                  {/* Image side */}
                  <div className="md:w-1/2 relative overflow-hidden min-h-[220px] md:min-h-0">
                    <img src={SVC_IMGS[i]} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" width={700} height={320} />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#060e18]/30 to-transparent pointer-events-none" />
                  </div>
                  {/* Text side */}
                  <div className="md:w-1/2 bg-[#0f393e] flex flex-col justify-center px-8 md:px-12 py-10">
                    <p className="text-[#00E5FF] text-xs font-semibold tracking-widest uppercase mb-3">
                      {`0${i + 1}`}
                    </p>
                    <h3 className="text-white text-2xl md:text-3xl font-bold mb-4 font-heading">
                      {service.title}
                    </h3>
                    <p className="text-white/60 text-base leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <Link to="/services" className="inline-flex items-center gap-2 text-[#00E5FF] font-semibold text-sm hover:gap-3 transition-all duration-200">
                      {service.cta} <ArrowRight size={15} />
                    </Link>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="py-24 bg-[#0a1420]">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div className="mb-14 text-center" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-[#00E5FF] text-sm font-semibold tracking-widest uppercase mb-3">
                {home.testimonials.sectionLabel}
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white font-heading">
                {home.testimonials.heading}
              </motion.h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {home.testimonials.items.map((testimonial, i) => <motion.div key={testimonial.id} className="bg-[#0f393e] rounded-2xl p-8 border border-white/5 relative overflow-hidden" initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              delay: i * 0.1,
              ease: 'easeOut' as const
            }} whileHover={{
              y: -4,
              transition: {
                duration: 0.2
              }
            }}>
                  {/* Decorative accent */}
                  <div className="absolute top-0 left-0 w-1 h-full bg-secondary" />
                  {/* Stars */}
                  <div className="flex gap-1 mb-5">
                    {Array.from({
                  length: testimonial.rating
                }).map((_, j) => <Star key={j} size={14} className="fill-secondary text-secondary" />)}
                  </div>
                  <blockquote className="text-white/75 text-base leading-relaxed mb-6 italic">
                    "{testimonial.quote}"
                  </blockquote>
                  <div>
                    <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                    <p className="text-[#00E5FF] text-xs mt-0.5">{testimonial.destination}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* ── VIDEO TESTIMONIALS ── */}
        <section className="py-24 bg-[#060e18] overflow-hidden">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div className="mb-14 text-center" initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-80px'
          }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-secondary text-sm font-semibold tracking-widest uppercase mb-3">
                Traveler Stories
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-white font-heading">
                Real Trips. Real Memories.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-white/50 text-lg mt-4 max-w-xl mx-auto">
                See what our travelers experienced — straight from the destination.
              </motion.p>
            </motion.div>

            {/* Cards grid — show 1 on mobile, 2 on md, 3 on lg, active card highlighted */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.7,
            ease: 'easeOut' as const
          }}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {VIDEO_TESTIMONIALS.map((t, i) => <div key={t.id} className={`relative rounded-2xl border bg-[#0a1420] cursor-pointer transition-[transform,opacity] duration-300 ${i === activeSlide ? 'border-secondary' : 'border-white/10'}`} style={{
                opacity: i === activeSlide ? 1 : 0.65,
                transform: i === activeSlide ? 'scale(1.03)' : 'scale(1)'
              }} onClick={() => {
                goTo(i);
                setPaused(true);
              }}>
                    {/* Left accent bar */}
                    <div className={`absolute top-0 left-0 w-1 h-full rounded-l-2xl z-10 ${i === activeSlide ? 'bg-secondary' : 'bg-transparent'}`} />
                    {/* Video */}
                    <div className="rounded-t-2xl overflow-hidden h-[220px]">
                      <DeferredVideo
                        src={t.videoSrc}
                        poster={imgSrc(DEST_IMGS[i], { width: 640, height: 440, fit: 'crop' })}
                        alt={`${t.name} travel story`}
                        className="w-full h-full object-cover block"
                        width={640}
                        height={440}
                        onPlay={() => trackWatchVideo(`Testimonial – ${t.name}`)}
                      />
                    </div>
                    {/* Overlay gradient */}
                    <div className="absolute pointer-events-none top-0 left-0 right-0 h-[220px] overlay-fade-up" />
                    {/* Play badge */}
                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center z-10 card-primary-badge">
                      <Play size={12} className="text-secondary fill-secondary ml-0.5" />
                    </div>
                    {/* Card content */}
                    <div className="p-5">
                      <div className="flex gap-1 mb-2">
                        {Array.from({
                      length: t.rating
                    }).map((_, j) => <Star key={j} size={11} className="fill-secondary text-secondary" />)}
                      </div>
                      <blockquote className="text-white/70 text-xs leading-relaxed mb-4 italic">
                        "{t.quote}"
                      </blockquote>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 card-gradient-border">
                          <span className="text-secondary text-xs font-bold">{t.name.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white font-semibold text-xs">{t.name}</p>
                          <p className="text-secondary text-xs">{t.destination}</p>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>

              {/* Dot indicators + prev/next */}
              <div className="flex items-center justify-center gap-4 mt-8">
                <button onClick={() => {
                goTo(activeSlide - 1);
                setPaused(true);
              }} className="w-9 h-9 rounded-full flex items-center justify-center transition-all card-glass" aria-label="Previous">
                  <ChevronLeft size={16} />
                </button>
                <div className="flex gap-2">
                  {VIDEO_TESTIMONIALS.map((_, i) => <button key={i} onClick={() => {
                  goTo(i);
                  setPaused(true);
                }} className={`h-2 w-2 rounded-full transition-[transform,background-color] duration-200 ${i === activeSlide ? 'scale-150 bg-primary' : 'bg-white/20'}`} aria-label={`Go to slide ${i + 1}`} aria-current={i === activeSlide ? 'true' : undefined} />)}
                </div>
                <button onClick={() => {
                goTo(activeSlide + 1);
                setPaused(true);
              }} className="w-9 h-9 rounded-full flex items-center justify-center transition-all card-glass" aria-label="Next">
                  <ChevronRightIcon size={16} />
                </button>
              </div>
            </motion.div>
          </div>
        </section>


        {/* ── CTA / QUOTE SECTION ── */}
        <section className="py-24 bg-[#060e18] relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#00E5FF]/5 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#ff1a00]/5 blur-3xl" />
          </div>

          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left — Headline */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-80px'
            }} variants={stagger}>
                <motion.p variants={fadeUp} className="text-[#00E5FF] text-sm font-semibold tracking-widest uppercase mb-4">
                  Start Planning
                </motion.p>
                <motion.h2 variants={fadeUp} className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6 font-heading">
                  {home.cta.heading}
                </motion.h2>
                <motion.p variants={fadeUp} className="text-white/60 text-lg leading-relaxed mb-8">
                  {home.cta.subheading}
                </motion.p>
                <motion.div variants={fadeUp} className="flex flex-wrap gap-6">
                  <div className="text-center">
                    <p className="font-heading text-[#00E5FF] text-3xl font-bold">500+</p>
                    <p className="text-white/50 text-sm mt-1">Destinations</p>
                  </div>
                  <div className="w-px bg-white/10" />
                  <div className="text-center">
                    <p className="font-heading text-[#00E5FF] text-3xl font-bold">20+</p>
                    <p className="text-white/50 text-sm mt-1">Years Experience</p>
                  </div>
                  <div className="w-px bg-white/10" />
                  <div className="text-center">
                    <p className="font-heading text-[#00E5FF] text-3xl font-bold">10k+</p>
                    <p className="text-white/50 text-sm mt-1">Happy Travelers</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* Right — Quote Form Preview */}
              <motion.div initial={{
              opacity: 0,
              x: 40
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              margin: '-80px'
            }} transition={{
              duration: 0.7,
              ease: 'easeOut' as const
            }}>
                <div className="bg-[#0f393e] rounded-2xl p-8 border border-white/10 shadow-2xl">
                  <h3 className="text-white text-2xl font-bold mb-6 font-heading">
                    {home.cta.formHeading}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="quote-preview-name" className="mb-1.5 block text-sm font-medium text-white">{home.cta.nameLabel}</label>
                      <input id="quote-preview-name" type="text" placeholder={home.cta.namePlaceholder} className="w-full bg-[#0a1420] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-muted-foreground text-sm focus:outline-none focus:border-[#00E5FF]/50 transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="quote-preview-destination" className="mb-1.5 block text-sm font-medium text-white">{home.cta.destinationLabel}</label>
                      <input id="quote-preview-destination" type="text" placeholder={home.cta.destinationPlaceholder} className="w-full bg-[#0a1420] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-muted-foreground text-sm focus:outline-none focus:border-[#00E5FF]/50 transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="quote-preview-email" className="mb-1.5 block text-sm font-medium text-white">{home.cta.emailLabel}</label>
                      <input id="quote-preview-email" type="email" placeholder={home.cta.emailPlaceholder} className="w-full bg-[#0a1420] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-muted-foreground text-sm focus:outline-none focus:border-[#00E5FF]/50 transition-colors" />
                    </div>
                    <Link to="/quote" className="block w-full py-4 bg-[#00E5FF] text-[#0a1420] font-bold text-base rounded-xl text-center hover:bg-[#00c8e0] transition-colors duration-200 mt-2">
                      {home.cta.submitLabel}
                    </Link>
                  </div>
                  <p className="text-white/30 text-xs text-center mt-4">
                    Free consultation · No commitment required
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── CONTACT FORM ── */}
        <ContactSection />

        {/* ── EMAIL SIGNUP ── */}
        <EmailSignupForm language="en" source="homepage" />
      </main>
    </>;
}
